import * as React from "react";
import {
  generateGame,
  type GameGenerator,
} from "@/game/game-generator/game-generator";
import type { MediaConfig } from "@fapinstructor/common";
import { createFileRoute } from "@tanstack/react-router";
import { GamePlayer } from "@/components/game-player";
import { GameProcessor } from "@/game/game-processor";
import { random } from "@/utils/random";
import { createBuilderTree } from "@/game/event-builders/builders/create-builder-tree";
import { useGameScriptDialog } from "@/hooks/use-game-script-dialog";
import { Center } from "@mantine/core";
import { StartGameCard } from "@/components/start-game-card";
import { startGameGenerator } from "@/game/start-game-generator";
import { accountSettingsQueryOptions } from "@/api/fapinstructor/account/get-account-settings";
import { useBeforeRouteLeave } from "@/hooks/use-before-route-leave";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/play/game-generator/",
)({
  loader: (opts) =>
    opts.context.queryClient.ensureQueryData(accountSettingsQueryOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const accountSettings = Route.useLoaderData();
  const gameGeneratorConfig = accountSettings.gameGeneratorConfig;
  const scriptId = gameGeneratorConfig.instructor.script ?? undefined;
  const voice = gameGeneratorConfig.instructor.voice ?? undefined;

  const scriptDialogQuery = useGameScriptDialog({
    scriptId,
    voice,
  });
  const scriptDialog = scriptDialogQuery.data;

  const [game, setGame] = React.useState<{
    processor: GameProcessor;
    media: MediaConfig;
    scriptId?: string;
  }>();
  const generatorRef = React.useRef<GameGenerator>(undefined);

  useBeforeRouteLeave(() => {
    if (game) {
      generatorRef.current?.return();
      generatorRef.current = undefined;
      game.processor.destroy();
    }
  });

  if (game) {
    return (
      <GamePlayer
        processor={game.processor}
        media={game.media}
        scriptId={game.scriptId}
      />
    );
  }

  return (
    <Center h="100%" mx="lg">
      <StartGameCard
        loading={scriptDialogQuery.isLoading}
        title="Generated Game"
        description="A generated game based off of your game configuration."
        splash="https://images.unsplash.com/photo-1570303345338-e1f0eddf4946?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3771&q=80"
        onStart={() => {
          const processor = new GameProcessor({
            dialog: scriptDialog,
            voice,
            tempo: accountSettings.tempo,
            beatPatternTempo: accountSettings.beatPatternTempo,
            edgingTempoPercent: accountSettings.edgingTempoPercent,
            chatSpeed: accountSettings.chat.speed,
          });

          const gameDuration =
            random.getRandomInclusiveInteger(
              ...gameGeneratorConfig.gameDuration,
            ) * 60;
          const builderTree = createBuilderTree(gameGeneratorConfig);
          const generator = generateGame(processor, builderTree, {
            duration: gameDuration,
            media: gameGeneratorConfig.media,
            tempo: accountSettings.tempo,
          });
          generatorRef.current = generator;

          startGameGenerator(processor, generator);
          setGame({
            processor,
            media: gameGeneratorConfig.media,
            scriptId,
          });
        }}
      />
    </Center>
  );
}
